import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  useUpdateCommunication,
  useGenerateReports,
} from "~/api/communications"
import { useQuarterlyReports } from "~/api/quarterly_reports"
import HouseholdsTab from "@/QuarterlyReportGenerator/HouseholdsTab"
import SecuritiesTab from "@/QuarterlyReportGenerator/SecuritiesTab"
import PortfolioCompaniesTab from "@/QuarterlyReportGenerator/PortfolioCompaniesTab"
import IntroLetterTab from "@/QuarterlyReportGenerator/IntroLetterTab"
import FinancialStatementTab from "@/QuarterlyReportGenerator/FinancialStatementTab"
import SectionsTab from "@/QuarterlyReportGenerator/SectionsTab"
import useDebouncedSelection from "@/hooks/useDebouncedSelection"
import { reportsPath } from "@/QuarterlyReportApp"
import { WandSparkles, FileText } from "lucide-react"
import { Button } from "@/ui/Button"
import { useCommunicationContext } from "@/QuarterlyReport/CommunicationContext"

const TAB_CONFIG = [
  { id: "households", label: "Households" },
  { id: "securities", label: "Securities" },
  { id: "portfolio_companies", label: "Portfolio Companies" },
  { id: "intro_letter", label: "Intro Letter" },
  { id: "financial_statement", label: "Financial Statement" },
  { id: "sections", label: "Report Sections" },
] as const

type TabId = (typeof TAB_CONFIG)[number]["id"]

const QuarterlyReportGenerator: React.FC = () => {
  const {
    communicationId,
    communication,
    isLoading: communicationLoading,
    error: communicationError,
  } = useCommunicationContext()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const paramTab = urlParams.get("active") as TabId
    return TAB_CONFIG.find((tab) => tab.id === paramTab)?.id || "households"
  })

  // Initialize URL and path. When the generator is initially loaded the page
  // may be replacing a DOM element from an existing page. This is a way to
  // force the URL to reflect the direct to link to the resource
  useEffect(() => {
    const currentPath = window.location.pathname
    if (!currentPath.includes(`/communications/${communicationId}`)) {
      const newPath = `/manage/communications/${communicationId}`
      window.history.pushState({ path: newPath }, "", newPath)
    }

    const url = new URL(window.location.href)
    if (!url.searchParams.has("active")) {
      url.searchParams.set("active", activeTab)
      window.history.pushState({ path: url.toString() }, "", url.toString())
    }
  }, [communicationId, activeTab])

  const updateUrlParam = (tabName: TabId) => {
    const url = new URL(window.location.href)
    url.searchParams.set("active", tabName)
    window.history.pushState({ path: url.toString() }, "", url.toString())
  }

  const updateCommunicationMutation = useUpdateCommunication()
  const {
    mutateAsync: generateReportsMutateAsync,
    isLoading: generateReportsLoading,
  } = useGenerateReports()

  const { data: reportsData } = useQuarterlyReports(communicationId, {
    refetchInterval: false,
  })

  const hasExistingReports = (reportsData?.reports?.length ?? 0) > 0

  const [initialDataLoaded, setInitialDataLoaded] = useState(false)

  useEffect(() => {
    if (communication && !communicationLoading && !initialDataLoaded) {
      setInitialDataLoaded(true)
    }
  }, [communication, communicationLoading, initialDataLoaded])

  const shouldAllowUpdates = initialDataLoaded

  const households = useDebouncedSelection({
    initialValue: communication?.household_ids || [],
    onUpdate: (value) =>
      updateCommunicationMutation.mutate({
        id: communicationId,
        household_ids: value,
      }),
    shouldUpdate: shouldAllowUpdates,
  })

  const securities = useDebouncedSelection({
    initialValue: communication?.security_exclusion_ids || [],
    onUpdate: (value) =>
      updateCommunicationMutation.mutate({
        id: communicationId,
        security_exclusion_ids: value,
      }),
    shouldUpdate: shouldAllowUpdates,
  })

  const portfolioCompanies = useDebouncedSelection({
    initialValue: communication?.portfolio_company_exclusion_ids || [],
    onUpdate: (value) =>
      updateCommunicationMutation.mutate({
        id: communicationId,
        portfolio_company_exclusion_ids: value,
      }),
    shouldUpdate: shouldAllowUpdates,
  })

  const handleTabClick = (tabId: TabId) => {
    setActiveTab(tabId)
    updateUrlParam(tabId)
  }

  const handleGenerateReports = async () => {
    if (!communication) return

    try {
      await generateReportsMutateAsync({ communicationId })
      navigate(reportsPath({ communicationId }))
    } catch (error) {
      console.error("Failed to generate reports:", error)
      alert("Failed to generate reports. Please try again.")
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "households":
        return (
          <HouseholdsTab
            communicationId={communicationId}
            selectedHouseholds={households.selected}
            onHouseholdSelectionChange={households.setSelected}
          />
        )
      case "securities":
        return (
          <SecuritiesTab
            communicationId={communicationId}
            selectedSecurities={securities.selected}
            onSecuritySelectionChange={securities.setSelected}
          />
        )
      case "portfolio_companies":
        return (
          <PortfolioCompaniesTab
            communicationId={communicationId}
            selectedPortfolioCompanies={portfolioCompanies.selected}
            onPortfolioCompanySelectionChange={portfolioCompanies.setSelected}
            communication={communication}
            isLoading={communicationLoading}
            error={communicationError}
          />
        )
      case "intro_letter":
        return <IntroLetterTab communicationId={communicationId} />
      case "financial_statement":
        return (
          <FinancialStatementTab
            communicationId={communicationId}
            year={communication?.year}
            quarter={communication?.quarter}
            communication={communication}
          />
        )
      case "sections":
        return (
          <SectionsTab
            communicationId={communicationId}
            communication={communication}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="quarterly-report-generator">
      <div className="quarterly-report-header">
        <div className="quarterly-report-tabs">
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => handleTabClick(tab.id)}
            >
              {tab.label}
            </button>
          ))}
          {hasExistingReports && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(reportsPath({ communicationId }))}
              icon={<FileText size={18} />}
              style={{ marginLeft: "auto" }}
            >
              View Reports
            </Button>
          )}
          <Button
            variant="filled"
            size="sm"
            onClick={handleGenerateReports}
            icon={<WandSparkles size={18} />}
            style={{ marginLeft: hasExistingReports ? "0.5rem" : "auto" }}
            disabled={
              generateReportsLoading ||
              communicationLoading ||
              !communication?.household_ids?.length
            }
          >
            {generateReportsLoading
              ? hasExistingReports
                ? "Regenerating..."
                : "Generating..."
              : hasExistingReports
              ? "Regenerate"
              : "Generate"}
          </Button>
        </div>
      </div>

      {generateReportsLoading ? (
        <div className="generating-overlay">
          <div className="generating-content">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h3>Creating Reports</h3>
            <p>Please wait while we create the reports for each household...</p>
          </div>
        </div>
      ) : (
        <div className="tab-content">{renderTabContent()}</div>
      )}
    </div>
  )
}

export default QuarterlyReportGenerator
